/**
 * DataCompareSetupDialog - Setup dialog for data comparison
 *
 * Allows user to configure source and target tables before opening
 * the data compare tab. Supports schema filtering and table selection.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Database, Server, Layers, Table2, ArrowLeftRight, TableProperties, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useConnectionStore } from '@/features/connections/store/connectionStore';
import { getDatabaseFromConnectionId } from '@/features/connections/utils/getDatabaseFromConnectionId';
// Deliberately NOT routed through `useDatabaseList` / the shared listing cache
// (phase 1 recorded gap — Docs/ObjectExplorer.md). This dialog needs a listing
// that is genuinely IN FLIGHT: it threads `onPhase` into `ListingLoadingLabel`
// ("Connecting to server…" → "Loading databases…"), and its failure specs pin a
// per-call `__holdListing { afterCall: 2 }` seam where the two panels on the
// SAME connection must produce two DISTINCT calls. Serving either panel from a
// shared cache entry removes both premises.
import {
  getDatabases,
  type DatabasesListingPhase,
  type DatabasesListingState,
} from '@/features/database-objects/services/databasesService';
import { getTables } from '@/features/database-objects/services/objectsService';
import { FallbackSelectContent } from '@/features/connection-diagnostics/components/FallbackSelectContent';
import { DatabaseListingFallbackBanner } from '@/features/connection-diagnostics/components/DatabaseListingFallbackBanner';
import { UnlistedDatabaseNotice } from '@/features/connection-diagnostics/components/UnlistedDatabaseNotice';
import { ListingLoadingLabel } from '../../common/components/ListingLoadingLabel';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { getEngineFromConnectionId } from '../utils/engineUtils';
import type {
  DataCompareEndpoint,
  DataCompareKeyMapping,
  DataCompareOptions,
  DataCompareValueMapping,
} from '../../core/types/workspaceTypes';
import { isConnectionEndpoint } from '../../core/types/workspaceTypes';
import type { DatabaseObject } from '@/features/database-objects/models/databaseObjectTypes';
import { openDataCompareWithEndpoints } from '../services/dataCompareNavigation';
import { RecentlyComparedLink, RecentlyComparedSection } from './RecentlyComparedSection';
import { useRecentlyComparedDisclosure } from '@/features/workspace/common/hooks/useRecentlyComparedDisclosure';
import type { RecentDataCompareEntry } from '../store/recentDataCompareStore';
import { safeConnectionName } from '@/types/safeConnectionDisplay';

type EndpointState = {
  connectionId?: string;
  database?: string;
  schema?: string;
  table?: string;
  databases: string[];
  /** Slice 5b: 'ok' / 'empty' / 'error' from the listing call. */
  databasesState?: DatabasesListingState;
  /**
   * Soporte C3: which phase the in-flight database listing is in, so the
   * trigger can say "Connecting to server…" vs "Loading databases…". Only
   * meaningful while `isLoadingDatabases` is true.
   */
  databasesPhase?: DatabasesListingPhase;
  tables: DatabaseObject[];
  /** Soporte C2: `getTables`' `error` string when the table listing failed. */
  tablesError?: string;
  isLoadingDatabases: boolean;
  isLoadingTables: boolean;
};


type DataCompareSetupDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill source from context menu */
  initialSource?: DataCompareEndpoint;
  /** Pre-fill target from context menu */
  initialTarget?: DataCompareEndpoint;
  /**
   * If provided, called with the selected endpoints instead of creating a new
   * tab — the "reconfigure the tab I'm looking at" path, opened by a
   * configured tab's own Change button.
   *
   * `config` is only present when the user picked a **recent comparison**,
   * which carries a full mapping snapshot. Confirming the dialog's own
   * source/target pickers passes nothing, because those pickers have no
   * mapping UI and the tab must re-derive mappings for the newly chosen
   * tables.
   */
  onConfirm?: (
    source: DataCompareEndpoint,
    target: DataCompareEndpoint,
    config?: RecentComparisonConfig,
  ) => void;
};

/** The parts of a recent comparison that survive beyond its two endpoints. */
export type RecentComparisonConfig = {
  keyMappings: DataCompareKeyMapping[];
  valueMappings: DataCompareValueMapping[];
  options?: DataCompareOptions;
};

const ALL_SCHEMAS = '__all_schemas__';
const ALL_TABLES = '__all_tables__';

export function DataCompareSetupDialog({
  open,
  onOpenChange,
  initialSource,
  initialTarget,
  onConfirm,
}: DataCompareSetupDialogProps) {
  const { connections, activeConnections } = useConnectionStore();
  const { addTab } = useWorkspaceStore();
  const { track } = useAnalytics();

  // Phase 3: the setup dialog only configures `'connection'` endpoints — narrow
  // the initial values to drop any blueprint-folder pre-fill (which has no UI here).
  const initialSourceConn = isConnectionEndpoint(initialSource) ? initialSource : undefined;
  const initialTargetConn = isConnectionEndpoint(initialTarget) ? initialTarget : undefined;

  // Source state
  const [source, setSource] = useState<EndpointState>({
    connectionId: initialSourceConn?.connectionId,
    database: initialSourceConn?.database,
    schema: initialSourceConn?.schema,
    table: initialSourceConn?.table,
    databases: [],
    tables: [],
    isLoadingDatabases: false,
    isLoadingTables: false,
  });

  // Target state
  const [target, setTarget] = useState<EndpointState>({
    connectionId: initialTargetConn?.connectionId,
    database: initialTargetConn?.database,
    schema: initialTargetConn?.schema,
    table: initialTargetConn?.table,
    databases: [],
    tables: [],
    isLoadingDatabases: false,
    isLoadingTables: false,
  });

  // Filter to only show connected connections
  const connectedConnections = useMemo(() => {
    return activeConnections
      .filter((ac) => ac.status === 'connected')
      .map((ac) => ({
        ...ac.connection,
        displayName: safeConnectionName(ac.connection),
      }));
  }, [activeConnections]);

  // Get engine types
  const sourceEngine = useMemo(() => {
    return getEngineFromConnectionId(source.connectionId, connections);
  }, [source.connectionId, connections]);

  const targetEngine = useMemo(() => {
    return getEngineFromConnectionId(target.connectionId, connections);
  }, [target.connectionId, connections]);

  // Extract unique schemas from tables
  const sourceSchemas = useMemo(() => {
    const schemaSet = new Set(source.tables.map((t) => t.schema));
    return Array.from(schemaSet).sort();
  }, [source.tables]);

  const targetSchemas = useMemo(() => {
    const schemaSet = new Set(target.tables.map((t) => t.schema));
    return Array.from(schemaSet).sort();
  }, [target.tables]);

  // Filter tables by selected schema
  const sourceFilteredTables = useMemo(() => {
    if (!source.schema || source.schema === ALL_SCHEMAS) return source.tables;
    return source.tables.filter((t) => t.schema === source.schema);
  }, [source.tables, source.schema]);

  const targetFilteredTables = useMemo(() => {
    if (!target.schema || target.schema === ALL_SCHEMAS) return target.tables;
    return target.tables.filter((t) => t.schema === target.schema);
  }, [target.tables, target.schema]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSource({
        connectionId: initialSourceConn?.connectionId,
        database: initialSourceConn?.database,
        schema: initialSourceConn?.schema,
        table: initialSourceConn?.table,
        databases: [],
        tables: [],
        isLoadingDatabases: false,
        isLoadingTables: false,
      });
      setTarget({
        connectionId: initialTargetConn?.connectionId,
        database: initialTargetConn?.database,
        schema: initialTargetConn?.schema,
        table: initialTargetConn?.table,
        databases: [],
        tables: [],
        isLoadingDatabases: false,
        isLoadingTables: false,
      });
    }
  }, [open, initialSourceConn, initialTargetConn]);

  // Fetch databases for source when connection changes
  useEffect(() => {
    // C2 (codex pre-PR review) — same normalise-on-early-return as the tables
    // effect below. This one also covers the dialog-reopen race: the reset
    // effect above clears `connectionId`, but its state update lands after
    // ALL effects in that commit have run, so this effect still fires for the
    // PREVIOUS connection, sets the flag, and is then cancelled by the
    // dependency change — leaving `isLoadingDatabases: true` on an endpoint
    // with no connection at all, which kept Swap disabled forever.
    if (!source.connectionId || !open) {
      setSource((prev) => (prev.isLoadingDatabases ? { ...prev, isLoadingDatabases: false } : prev));
      return;
    }

    let cancelled = false;

    async function fetchDatabases() {
      setSource((prev) => ({ ...prev, isLoadingDatabases: true, databasesPhase: undefined }));
      try {
        // Soporte C3 — phase reports drive the trigger label; they never
        // affect the result, so a failure in either phase still lands in the
        // C2 error surface below.
        // Direct call, not the shared cache — see the `getDatabases` import note.
        const result = await getDatabases(source.connectionId!, sourceEngine, {
          onPhase: (phase) => {
            if (!cancelled) setSource((prev) => ({ ...prev, databasesPhase: phase }));
          },
        });
        if (cancelled) return;
        setSource((prev) => ({
          ...prev,
          databases: result.state === 'ok' ? result.databases : [],
          databasesState: result.state,
          isLoadingDatabases: false,
          databasesPhase: undefined,
        }));
      } catch {
        if (!cancelled) {
          setSource((prev) => ({
            ...prev,
            databases: [],
            databasesState: 'error',
            isLoadingDatabases: false,
            databasesPhase: undefined,
          }));
        }
      }
    }

    fetchDatabases();
    return () => { cancelled = true; };
  }, [source.connectionId, sourceEngine, open]);

  // Fetch databases for target when connection changes
  useEffect(() => {
    // C2 — see the source-side comment above.
    if (!target.connectionId || !open) {
      setTarget((prev) => (prev.isLoadingDatabases ? { ...prev, isLoadingDatabases: false } : prev));
      return;
    }

    let cancelled = false;

    async function fetchDatabases() {
      setTarget((prev) => ({ ...prev, isLoadingDatabases: true, databasesPhase: undefined }));
      try {
        // Soporte C3 — see the source-side comment above. Direct call, not the
        // shared cache — see the `getDatabases` import note.
        const result = await getDatabases(target.connectionId!, targetEngine, {
          onPhase: (phase) => {
            if (!cancelled) setTarget((prev) => ({ ...prev, databasesPhase: phase }));
          },
        });
        if (cancelled) return;
        setTarget((prev) => ({
          ...prev,
          databases: result.state === 'ok' ? result.databases : [],
          databasesState: result.state,
          isLoadingDatabases: false,
          databasesPhase: undefined,
        }));
      } catch {
        if (!cancelled) {
          setTarget((prev) => ({
            ...prev,
            databases: [],
            databasesState: 'error',
            isLoadingDatabases: false,
            databasesPhase: undefined,
          }));
        }
      }
    }

    fetchDatabases();
    return () => { cancelled = true; };
  }, [target.connectionId, targetEngine, open]);

  // Fetch tables for source when database changes
  useEffect(() => {
    // C2 (codex pre-PR review) — normalise the flag on EVERY early return.
    // The cleanup below only flips its local `cancelled`, so a listing that
    // was in flight when the user changed the selection leaves
    // `isLoadingTables: true` riding the `...prev` spread in the selection
    // handlers. If this run then bails (the newly-picked connection has no
    // saved default database yet) nothing else would clear it, and the Swap
    // button would stay disabled with a "wait for lists to finish loading"
    // tooltip for a listing that no longer exists. The identity guard keeps
    // this from looping.
    if (!source.connectionId || !source.database || !open) {
      setSource((prev) => (prev.isLoadingTables ? { ...prev, isLoadingTables: false } : prev));
      return;
    }

    let cancelled = false;

    async function fetchTables() {
      setSource((prev) => ({ ...prev, isLoadingTables: true, tablesError: undefined }));
      try {
        const result = await getTables(source.connectionId!, source.database!, sourceEngine);
        if (cancelled) return;

        const tables = result.tables;
        const uniqueSchemas = new Set(tables.map((t) => t.schema));

        // Auto-select schema if only one exists
        const autoSchema = uniqueSchemas.size === 1 ? tables[0]?.schema : undefined;

        setSource((prev) => ({
          ...prev,
          tables,
          schema: autoSchema ?? prev.schema,
          isLoadingTables: false,
          tablesError: result.error,
        }));
      } catch (err) {
        if (!cancelled) {
          setSource((prev) => ({
            ...prev,
            tables: [],
            isLoadingTables: false,
            tablesError: err instanceof Error ? err.message : 'Failed to load tables',
          }));
        }
      }
    }

    fetchTables();
    return () => { cancelled = true; };
  }, [source.connectionId, source.database, sourceEngine, open]);

  // Fetch tables for target when database changes
  useEffect(() => {
    // C2 — see the source-side comment above.
    if (!target.connectionId || !target.database || !open) {
      setTarget((prev) => (prev.isLoadingTables ? { ...prev, isLoadingTables: false } : prev));
      return;
    }

    let cancelled = false;

    async function fetchTables() {
      setTarget((prev) => ({ ...prev, isLoadingTables: true, tablesError: undefined }));
      try {
        const result = await getTables(target.connectionId!, target.database!, targetEngine);
        if (cancelled) return;

        const tables = result.tables;
        const uniqueSchemas = new Set(tables.map((t) => t.schema));

        // Auto-select schema if only one exists
        const autoSchema = uniqueSchemas.size === 1 ? tables[0]?.schema : undefined;

        setTarget((prev) => ({
          ...prev,
          tables,
          schema: autoSchema ?? prev.schema,
          isLoadingTables: false,
          tablesError: result.error,
        }));
      } catch (err) {
        if (!cancelled) {
          setTarget((prev) => ({
            ...prev,
            tables: [],
            isLoadingTables: false,
            tablesError: err instanceof Error ? err.message : 'Failed to load tables',
          }));
        }
      }
    }

    fetchTables();
    return () => { cancelled = true; };
  }, [target.connectionId, target.database, targetEngine, open]);

  // Auto-select matching schema/table on target when target tables are loaded
  // and source already has selections
  useEffect(() => {
    if (!open || target.tables.length === 0) return;

    setTarget((prev) => {
      let updated = { ...prev };
      let changed = false;

      // If source has schema selected and target doesn't, try to match
      if (source.schema && !prev.schema) {
        const targetSchemas = new Set(prev.tables.map((t) => t.schema));
        if (targetSchemas.has(source.schema)) {
          updated = { ...updated, schema: source.schema };
          changed = true;
        }
      }

      // If source has table selected and target has same schema but no table, try to match
      if (source.table && updated.schema === source.schema && !prev.table) {
        const targetTablesInSchema = prev.tables.filter((t) => t.schema === updated.schema);
        const matchingTable = targetTablesInSchema.find((t) => t.name === source.table);
        if (matchingTable) {
          updated = { ...updated, table: source.table };
          changed = true;
        }
      }

      return changed ? updated : prev;
    });
  }, [open, target.tables, source.schema, source.table]);

  // Handlers
  // C2 (codex pre-PR review) — every handler that changes an endpoint's
  // connection/database hands the tables fetch effect a new identity, so the
  // in-flight listing for the OLD identity stops owning `isLoadingTables`.
  // Reset it here rather than inheriting it through `...prev`; the effect
  // sets it back to true on the very next commit whenever it actually runs.
  // (`isLoadingDatabases` deliberately stays untouched: its effect's deps
  // always change on a connection pick, so it is always re-owned.)
  const handleSourceConnectionChange = useCallback((connectionId: string) => {
    setSource((prev) => ({
      ...prev,
      connectionId,
      database: getDatabaseFromConnectionId(connectionId, connections, activeConnections),
      schema: undefined,
      table: undefined,
      databases: [],
      tables: [],
      tablesError: undefined,
      isLoadingTables: false,
    }));
  }, [connections, activeConnections]);

  const handleSourceDatabaseChange = useCallback((database: string) => {
    setSource((prev) => ({
      ...prev,
      database,
      schema: undefined,
      table: undefined,
      tables: [],
      tablesError: undefined,
      isLoadingTables: false,
    }));
  }, []);

  const handleSourceSchemaChange = useCallback((schema: string) => {
    const selectedSchema = schema === ALL_SCHEMAS ? undefined : schema;
    setSource((prev) => ({
      ...prev,
      schema: selectedSchema,
      table: undefined,
    }));

    // Auto-select matching schema on target if available
    if (selectedSchema) {
      setTarget((prev) => {
        const targetSchemas = new Set(prev.tables.map((t) => t.schema));
        if (targetSchemas.has(selectedSchema) && !prev.schema) {
          return { ...prev, schema: selectedSchema, table: undefined };
        }
        return prev;
      });
    }
  }, []);

  const handleSourceTableChange = useCallback((table: string) => {
    const selectedTable = table === ALL_TABLES ? undefined : table;
    setSource((prev) => ({
      ...prev,
      table: selectedTable,
    }));

    // Auto-select matching table on target if available in same schema
    if (selectedTable) {
      setTarget((prev) => {
        if (!prev.schema) return prev;
        const targetTablesInSchema = prev.tables.filter((t) => t.schema === prev.schema);
        const matchingTable = targetTablesInSchema.find((t) => t.name === selectedTable);
        if (matchingTable && !prev.table) {
          return { ...prev, table: selectedTable };
        }
        return prev;
      });
    }
  }, []);

  const handleTargetConnectionChange = useCallback((connectionId: string) => {
    setTarget((prev) => ({
      ...prev,
      connectionId,
      database: getDatabaseFromConnectionId(connectionId, connections, activeConnections),
      schema: undefined,
      table: undefined,
      databases: [],
      tables: [],
      tablesError: undefined,
      isLoadingTables: false,
    }));
  }, [connections, activeConnections]);

  const handleTargetDatabaseChange = useCallback((database: string) => {
    setTarget((prev) => ({
      ...prev,
      database,
      schema: undefined,
      table: undefined,
      tables: [],
      tablesError: undefined,
      isLoadingTables: false,
    }));
  }, []);

  const handleTargetSchemaChange = useCallback((schema: string) => {
    setTarget((prev) => ({
      ...prev,
      schema: schema === ALL_SCHEMAS ? undefined : schema,
      table: undefined,
    }));
  }, []);

  const handleTargetTableChange = useCallback((table: string) => {
    setTarget((prev) => ({
      ...prev,
      table: table === ALL_TABLES ? undefined : table,
    }));
  }, []);

  // C4 (Soporte fixes round 2, Wave C) — `handleSwap` does an atomic
  // whole-object exchange of `source`/`target`, which correctly lets React's
  // own effect-cleanup `cancelled` guard cancel an orphaned in-flight fetch
  // whenever the swap actually changes a slot's `connectionId`/`database`.
  // But when BOTH sides share the same connection (and/or database) — a
  // mainstream Data Compare config ("diff two tables on the same server") —
  // the swap does NOT change that slot's fetch-effect dependencies, so the
  // effect never re-runs: a moved-in `isLoadingDatabases`/`isLoadingTables:
  // true` flag ends up with no owning promise (pends forever, Retry stays
  // hidden since it's gated on `!isLoading*`), and an already-in-flight
  // fetch for the OTHER (still-owning) slot can resolve later and silently
  // clobber the freshly-swapped-in endpoint's loaded tables/error state.
  // Simplest fix (endorsed over a fetch-token/re-keying scheme): disable
  // swap entirely while any of the four loading flags are true —
  // `isAnyEndpointLoading` also disables the button in the UI below. This
  // guard is defense-in-depth for the disabled button.
  const isAnyEndpointLoading =
    source.isLoadingDatabases ||
    source.isLoadingTables ||
    target.isLoadingDatabases ||
    target.isLoadingTables;

  const handleSwap = useCallback(() => {
    if (isAnyEndpointLoading) return;
    const sourceSnapshot = { ...source };
    const targetSnapshot = { ...target };
    setSource(targetSnapshot);
    setTarget(sourceSnapshot);
  }, [source, target, isAnyEndpointLoading]);

  // Soporte C2 — Retry handlers for the inline listing-failure surfaces below.
  // Mirror the corresponding fetch effects above rather than sharing code
  // with them, since the effects are keyed to automatic dependency changes
  // (connection/database) while these are explicit user-initiated re-fetches.
  // Each handler guards its state writes against `prev.connectionId`
  // (and `prev.database`, for the table/schema variants) so a stale
  // in-flight retry — the user clicked Retry then switched connection/
  // database before it resolved — can't clobber the new endpoint's state.
  // Mirrors the `cancelled` flag the automatic effects use, adapted for a
  // one-shot user-triggered call that has no cleanup function to hook.
  const retrySourceDatabases = useCallback(() => {
    if (!source.connectionId) return;
    track('feature:compare_setup_retry', { dialog: 'data', target: 'databases' });
    const connectionId = source.connectionId;
    setSource((prev) =>
      prev.connectionId !== connectionId
        ? prev
        : { ...prev, isLoadingDatabases: true, databasesPhase: undefined }
    );
    // Retry = a fresh in-flight listing by definition; never the shared cache.
    getDatabases(connectionId, sourceEngine, {
      onPhase: (phase) =>
        setSource((prev) =>
          prev.connectionId !== connectionId ? prev : { ...prev, databasesPhase: phase }
        ),
    })
      .then((result) => {
        setSource((prev) =>
          prev.connectionId !== connectionId
            ? prev
            : {
                ...prev,
                databases: result.state === 'ok' ? result.databases : [],
                databasesState: result.state,
                isLoadingDatabases: false,
                databasesPhase: undefined,
              }
        );
      })
      .catch(() => {
        setSource((prev) =>
          prev.connectionId !== connectionId
            ? prev
            : {
                ...prev,
                databases: [],
                databasesState: 'error',
                isLoadingDatabases: false,
                databasesPhase: undefined,
              }
        );
      });
  }, [source.connectionId, sourceEngine, track]);

  const retryTargetDatabases = useCallback(() => {
    if (!target.connectionId) return;
    track('feature:compare_setup_retry', { dialog: 'data', target: 'databases' });
    const connectionId = target.connectionId;
    setTarget((prev) =>
      prev.connectionId !== connectionId
        ? prev
        : { ...prev, isLoadingDatabases: true, databasesPhase: undefined }
    );
    // Retry = a fresh in-flight listing by definition; never the shared cache.
    getDatabases(connectionId, targetEngine, {
      onPhase: (phase) =>
        setTarget((prev) =>
          prev.connectionId !== connectionId ? prev : { ...prev, databasesPhase: phase }
        ),
    })
      .then((result) => {
        setTarget((prev) =>
          prev.connectionId !== connectionId
            ? prev
            : {
                ...prev,
                databases: result.state === 'ok' ? result.databases : [],
                databasesState: result.state,
                isLoadingDatabases: false,
                databasesPhase: undefined,
              }
        );
      })
      .catch(() => {
        setTarget((prev) =>
          prev.connectionId !== connectionId
            ? prev
            : {
                ...prev,
                databases: [],
                databasesState: 'error',
                isLoadingDatabases: false,
                databasesPhase: undefined,
              }
        );
      });
  }, [target.connectionId, targetEngine, track]);

  const retrySourceTables = useCallback(() => {
    if (!source.connectionId || !source.database) return;
    track('feature:compare_setup_retry', { dialog: 'data', target: 'tables' });
    const connectionId = source.connectionId;
    const database = source.database;
    const isStale = (prev: EndpointState) => prev.connectionId !== connectionId || prev.database !== database;
    setSource((prev) => (isStale(prev) ? prev : { ...prev, isLoadingTables: true, tablesError: undefined }));
    getTables(connectionId, database, sourceEngine)
      .then((result) => {
        const tables = result.tables;
        const uniqueSchemas = new Set(tables.map((t) => t.schema));
        const autoSchema = uniqueSchemas.size === 1 ? tables[0]?.schema : undefined;
        setSource((prev) =>
          isStale(prev)
            ? prev
            : {
                ...prev,
                tables,
                schema: autoSchema ?? prev.schema,
                isLoadingTables: false,
                tablesError: result.error,
              }
        );
      })
      .catch((err) => {
        setSource((prev) =>
          isStale(prev)
            ? prev
            : {
                ...prev,
                tables: [],
                isLoadingTables: false,
                tablesError: err instanceof Error ? err.message : 'Failed to load tables',
              }
        );
      });
  }, [source.connectionId, source.database, sourceEngine, track]);

  const retryTargetTables = useCallback(() => {
    if (!target.connectionId || !target.database) return;
    track('feature:compare_setup_retry', { dialog: 'data', target: 'tables' });
    const connectionId = target.connectionId;
    const database = target.database;
    const isStale = (prev: EndpointState) => prev.connectionId !== connectionId || prev.database !== database;
    setTarget((prev) => (isStale(prev) ? prev : { ...prev, isLoadingTables: true, tablesError: undefined }));
    getTables(connectionId, database, targetEngine)
      .then((result) => {
        const tables = result.tables;
        const uniqueSchemas = new Set(tables.map((t) => t.schema));
        const autoSchema = uniqueSchemas.size === 1 ? tables[0]?.schema : undefined;
        setTarget((prev) =>
          isStale(prev)
            ? prev
            : {
                ...prev,
                tables,
                schema: autoSchema ?? prev.schema,
                isLoadingTables: false,
                tablesError: result.error,
              }
        );
      })
      .catch((err) => {
        setTarget((prev) =>
          isStale(prev)
            ? prev
            : {
                ...prev,
                tables: [],
                isLoadingTables: false,
                tablesError: err instanceof Error ? err.message : 'Failed to load tables',
              }
        );
      });
  }, [target.connectionId, target.database, targetEngine, track]);

  const handleOpenCompare = useCallback(() => {
    // Build endpoint objects. The dialog gates the OK button on `canOpen`, which
    // requires all four fields to be populated — so the `?? ''` defaults below
    // only run on an inconsistent close path.
    const sourceEndpoint: DataCompareEndpoint = {
      kind: 'connection',
      connectionId: source.connectionId ?? '',
      database: source.database ?? '',
      schema: source.schema ?? '',
      table: source.table ?? '',
    };

    const targetEndpoint: DataCompareEndpoint = {
      kind: 'connection',
      connectionId: target.connectionId ?? '',
      database: target.database ?? '',
      schema: target.schema ?? '',
      table: target.table ?? '',
    };

    // If onConfirm is provided, use it (update mode for existing tab)
    if (onConfirm) {
      onConfirm(sourceEndpoint, targetEndpoint);
      onOpenChange(false);
      return;
    }

    // Otherwise create a new tab
    const sourceConn = connectedConnections.find((c) => c.id === source.connectionId);
    const targetConn = connectedConnections.find((c) => c.id === target.connectionId);
    const sourceLabel = source.table || source.schema || source.database || sourceConn?.displayName || 'Source';
    const targetLabel = target.table || target.schema || target.database || targetConn?.displayName || 'Target';

    addTab({
      title: `Compare: ${sourceLabel} → ${targetLabel}`,
      type: 'data-compare',
      content: {
        source: sourceEndpoint,
        target: targetEndpoint,
      },
    });

    onOpenChange(false);
  }, [source, target, connectedConnections, addTab, onOpenChange, onConfirm]);

  // Task B4 (Soporte3, F07) — selecting a "recently compared" entry opens a
  // brand-new, fully-configured-but-idle tab straight away, rather than
  // prefilling this dialog's own source/target fields the way Schema
  // Compare's `handleSelectRecent` does. Two reasons this dialog can't mirror
  // that: (1) it has no UI for key/value column mappings at all — prefilling
  // only source/target would silently drop the very thing the user is trying
  // to get back; (2) `openDataCompareWithEndpoints` already documents itself
  // as "always creates a fresh tab" (mappings are table-specific), so a
  // recent-select follows that same established contract rather than
  // threading a new "reuse this tab" path through it. `autoCompare` is
  // omitted so the tab opens configured-but-idle — the B3 banner's "Run
  // Compare" button is the one-click path from there, not an automatic run.
  const handleSelectRecent = useCallback(
    (entry: RecentDataCompareEntry) => {
      // B5 correction to B4 — when this dialog was opened by a configured
      // tab's own "Change" button (`onConfirm` present), the user is
      // reconfiguring the tab in front of them. B4 opened a SECOND tab here
      // and left the original untouched, which reads as a bug. Route through
      // the update-existing path instead, carrying the mappings the entry
      // exists to preserve — `onConfirm` alone would reset them to [].
      //
      // With no `onConfirm` (the toolbar's "Data Compare", which has no tab to
      // update) a fresh tab is still correct, and
      // `openDataCompareWithEndpoints`' always-fresh contract still applies.
      if (onConfirm) {
        onConfirm(entry.source, entry.target, {
          keyMappings: entry.keyMappings,
          valueMappings: entry.valueMappings,
          options: entry.options,
        });
        onOpenChange(false);
        return;
      }
      openDataCompareWithEndpoints({
        source: entry.source,
        target: entry.target,
        keyMappings: entry.keyMappings,
        valueMappings: entry.valueMappings,
        options: entry.options,
      });
      onOpenChange(false);
    },
    [onConfirm, onOpenChange],
  );

  // Validation
  const canOpen = useMemo(() => {
    const hasSource = source.connectionId && source.database && source.schema && source.table;
    const hasTarget = target.connectionId && target.database && target.schema && target.table;
    return hasSource && hasTarget;
  }, [source, target]);

  const isSqliteSource = sourceEngine === 'sqlite';
  const isSqliteTarget = targetEngine === 'sqlite';

  // Show schema selector when:
  // - Database is selected AND (tables are loading OR schemas exist OR initial schema was provided)
  const showSourceSchemaSelector = !isSqliteSource && source.database &&
    (source.isLoadingTables || sourceSchemas.length > 0 || !!source.schema);
  const showTargetSchemaSelector = !isSqliteTarget && target.database &&
    (target.isLoadingTables || targetSchemas.length > 0 || !!target.schema);

  // Show table selector when:
  // - Schema is selected AND (tables are loading OR tables exist OR initial table was provided)
  const showSourceTableSelector = source.schema &&
    (source.isLoadingTables || sourceFilteredTables.length > 0 || !!source.table);
  const showTargetTableSelector = target.schema &&
    (target.isLoadingTables || targetFilteredTables.length > 0 || !!target.table);

  // R3-10 — same disclosure as Schema Compare's setup dialog (R3-4), from the
  // same shared hook so the two cannot drift: expanded until an endpoint is
  // chosen, then one click away from the header link.
  const recents = useRecentlyComparedDisclosure({
    hasSelection: !!source.connectionId || !!target.connectionId,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* R4-4 — cap the height and make the BODY the scroller, so the header
          and the footer's Cancel / Open Compare stay put.

          Unbounded, the dialog grew with its content: expanding "Recently
          compared" on a short window pushed the endpoint panels past the
          viewport and took the footer with them, so the primary action was
          off-screen with nothing to scroll. Sizing is deliberately identical
          to the Schema Compare setup dialog (`max-h-[90vh] flex flex-col` +
          `flex-1 min-h-0 overflow-y-auto -mx-6 px-6`), which already solved
          this — the two setup dialogs are siblings and should not drift.

          `min-h-0` is the load-bearing half: a flex child's default
          `min-height:auto` refuses to shrink below its content, so
          `overflow-y-auto` alone would never produce a scrollbar. The
          `-mx-6 px-6` pair lets the scroll track sit at the dialog edge while
          the content keeps its padding. */}
      <DialogContent
        className="max-w-4xl max-h-[90vh] flex flex-col"
        data-testid="data-compare-setup-dialog"
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-2">
              <TableProperties className="h-5 w-5" />
              Data Compare Setup
            </DialogTitle>
            {/* R3-10 — collapsed recents sit at the top-right of the header,
                the same slot Schema Compare uses (there, immediately left of
                its "Compare whole database" checkbox). */}
            {recents.showLink && (
              <RecentlyComparedLink open={recents.open} onToggle={recents.toggle} />
            )}
          </div>
          <DialogDescription>
            Select source and target tables to compare their data.
          </DialogDescription>
        </DialogHeader>

        {/* R4-4 — everything between the header and the footer scrolls as one
            region. Recents is INSIDE it on purpose: expanding it is what
            overflows a short window, so it has to be a thing that scrolls, not
            a thing that pushes. */}
        <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
        {/* F11 — recents is an input, not a footer note. Here it replaces the
            form outright (handleSelectRecent opens a configured tab), which is
            all the more reason it belongs above it, not between it and its
            own submit button. R3-10 — collapsed once an endpoint is chosen. */}
        {recents.open && <RecentlyComparedSection onSelect={handleSelectRecent} />}

        <div className="flex gap-4 items-start py-4">
          {/* Source Panel */}
          <div className="flex-1 border rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium">Source</h3>

            {/* Connection */}
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Server className="h-3 w-3" />
                Connection
              </label>
              <Select
                value={source.connectionId ?? ''}
                onValueChange={handleSourceConnectionChange}
              >
                <SelectTrigger className="w-full" data-testid="source-connection-select">
                  <SelectValue placeholder="Select connection..." />
                </SelectTrigger>
                <SelectContent>
                  {connectedConnections.length === 0 ? (
                    <SelectItem value="_none" disabled>
                      No active connections
                    </SelectItem>
                  ) : (
                    connectedConnections.map((conn) => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {safeConnectionName(conn)}
                        {conn.engine && conn.engine !== 'mssql' && (
                          <span className="ml-1 text-xs text-muted-foreground uppercase">
                            ({conn.engine})
                          </span>
                        )}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Database */}
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Database className="h-3 w-3" />
                Database
              </label>
              {/* Soporte C3 — the trigger stays ENABLED while loading (it
                  used to be `disabled`, so a user staring at a minutes-long
                  cold connect couldn't even open it) and the spinner names
                  the phase it is waiting on. */}
              <Select
                value={source.database ?? ''}
                onValueChange={handleSourceDatabaseChange}
                disabled={!source.connectionId}
              >
                <SelectTrigger className="w-full" data-testid="source-database-select">
                  {source.isLoadingDatabases ? (
                    <ListingLoadingLabel target="databases" phase={source.databasesPhase} />
                  ) : (
                    <SelectValue placeholder="Select database..." />
                  )}
                </SelectTrigger>
                <SelectContent>
                  <FallbackSelectContent
                    connectionId={source.connectionId}
                    state={source.databasesState}
                    databases={source.databases}
                    isLoading={source.isLoadingDatabases}
                    selectedDatabase={source.database}
                    testIdPrefix="source"
                  />
                </SelectContent>
              </Select>
            </div>

            {/* #91 — the picked database is not one this connection lists
                (dropped/renamed, invisible to this login, or hidden by the
                connection's own database limit). Renders nothing otherwise. */}
            <UnlistedDatabaseNotice
              connectionId={source.connectionId}
              database={source.database}
              databases={source.databases}
              state={source.databasesState}
              isLoading={source.isLoadingDatabases}
              testIdPrefix="source"
            />

            {/* Soporte C2 — database-listing failure, visible without opening
                the (closed) dropdown above. */}
            {source.databasesState === 'error' && !source.isLoadingDatabases && (
              <div className="flex flex-col gap-1.5">
                <DatabaseListingFallbackBanner
                  connectionId={source.connectionId!}
                  variant="row"
                  trigger="list_failure"
                  data-testid="source-databases-fallback-banner"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 self-start px-2 text-xs"
                  onClick={retrySourceDatabases}
                  data-testid="source-databases-retry"
                >
                  Retry
                </Button>
              </div>
            )}

            {/* Schema */}
            {showSourceSchemaSelector && (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Layers className="h-3 w-3" />
                  Schema
                </label>
                {/* Soporte C3 — `getTables` has no separable connect step, so
                    this spinner carries the single honest label rather than a
                    phase. Trigger stays enabled while loading. */}
                <Select
                  value={source.schema ?? ''}
                  onValueChange={handleSourceSchemaChange}
                >
                  <SelectTrigger className="w-full" data-testid="source-schema-select">
                    {source.isLoadingTables ? (
                      <ListingLoadingLabel target="tables" />
                    ) : (
                      <SelectValue placeholder="Select schema..." />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {/* Soporte C3 fix-before-merge (final-review-waveC-report.md
                        #11) — the trigger stays enabled while loading, so a
                        user can open this popper mid-load; without a loading
                        row it renders nothing (blank popper) since
                        `sourceSchemas` is still empty at that point. */}
                    {source.isLoadingTables ? (
                      <SelectItem value="_loading" disabled data-testid="source-schema-listing-loading">
                        <ListingLoadingLabel target="tables" />
                      </SelectItem>
                    ) : (
                      sourceSchemas.map((schema) => (
                        <SelectItem key={schema} value={schema}>
                          {schema || '(default)'}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Soporte C2 — table-listing failure. Not gated on
                `!showSourceSchemaSelector`: a prefilled `source.schema`
                (e.g. "Compare Data" from an Object Explorer table context
                menu, see `initialSourceConn` above) keeps that selector
                showing even when the underlying listing just failed, which
                would otherwise hide this error on the dialog's most common
                entry path.

                C4 (codex pre-PR review) — and NOT gated on `!isSqliteSource`
                either. That term was copied from Schema Compare, where it is
                a harmless no-op because that dialog's schemas effect skips
                SQLite outright. Here `getTables` genuinely runs for SQLite,
                so the gate suppressed the error for the one engine that can
                reach it: no schema row (correct — SQLite has no schemas), no
                table row (`autoSchema` is never set from an empty array), no
                error, no Retry. Only genuinely schema-shaped controls, like
                the Schema selector below, stay engine-gated. */}
            {source.database &&
              !source.isLoadingTables && source.tablesError && (
              <div
                className="flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive"
                data-testid="source-tables-error"
              >
                <span className="flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Couldn&apos;t load schema/table info for this database.
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 shrink-0 px-2 text-xs"
                  onClick={retrySourceTables}
                  data-testid="source-tables-retry"
                >
                  Retry
                </Button>
              </div>
            )}

            {/* Table */}
            {showSourceTableSelector && (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Table2 className="h-3 w-3" />
                  Table
                </label>
                {/* `portal` is load-bearing: Table is the last field in the
                    panel, and the dialog body it sits in is a scroll container
                    (`flex-1 min-h-0 overflow-y-auto`, R4-4 above). The default
                    inline menu is clipped to that container's box, so the
                    dropdown showed its search row and nothing else — the
                    options were unreachable. Portal mode renders the menu into
                    the dialog frame (still inside its focus/dismiss layer) and
                    flips it above the trigger when the room below is too
                    small, which on a tall dialog it usually is. */}
                <SearchableSelect
                  value={source.table ?? ''}
                  onValueChange={handleSourceTableChange}
                  options={sourceFilteredTables.map((table) => ({
                    value: table.name,
                    label: table.name,
                  }))}
                  placeholder="Select table..."
                  searchPlaceholder="Search tables..."
                  portal
                  data-testid="source-table-select"
                />
              </div>
            )}
          </div>

          {/* Swap Button */}
          <div className="flex items-center pt-8">
            <Tooltip>
              <TooltipTrigger asChild>
                {/* C4 — a disabled shadcn Button has `pointer-events-none`,
                    which blocks Radix's hover trigger entirely, so the
                    wrapping span carries the trigger role instead (mirrors
                    FixedValueDisabledButton.tsx's "why disabled" pattern). */}
                <span tabIndex={isAnyEndpointLoading ? 0 : -1}>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleSwap}
                    disabled={isAnyEndpointLoading}
                    data-testid="swap-button"
                    aria-label="Swap source and target"
                  >
                    <ArrowLeftRight className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {isAnyEndpointLoading
                  ? 'Wait for lists to finish loading'
                  : 'Swap source and target'}
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Target Panel */}
          <div className="flex-1 border rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium">Target</h3>

            {/* Connection */}
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Server className="h-3 w-3" />
                Connection
              </label>
              <Select
                value={target.connectionId ?? ''}
                onValueChange={handleTargetConnectionChange}
              >
                <SelectTrigger className="w-full" data-testid="target-connection-select">
                  <SelectValue placeholder="Select connection..." />
                </SelectTrigger>
                <SelectContent>
                  {connectedConnections.length === 0 ? (
                    <SelectItem value="_none" disabled>
                      No active connections
                    </SelectItem>
                  ) : (
                    connectedConnections.map((conn) => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {safeConnectionName(conn)}
                        {conn.engine && conn.engine !== 'mssql' && (
                          <span className="ml-1 text-xs text-muted-foreground uppercase">
                            ({conn.engine})
                          </span>
                        )}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Database */}
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Database className="h-3 w-3" />
                Database
              </label>
              {/* Soporte C3 — see the source panel's comment above. */}
              <Select
                value={target.database ?? ''}
                onValueChange={handleTargetDatabaseChange}
                disabled={!target.connectionId}
              >
                <SelectTrigger className="w-full" data-testid="target-database-select">
                  {target.isLoadingDatabases ? (
                    <ListingLoadingLabel target="databases" phase={target.databasesPhase} />
                  ) : (
                    <SelectValue placeholder="Select database..." />
                  )}
                </SelectTrigger>
                <SelectContent>
                  <FallbackSelectContent
                    connectionId={target.connectionId}
                    state={target.databasesState}
                    databases={target.databases}
                    isLoading={target.isLoadingDatabases}
                    selectedDatabase={target.database}
                    testIdPrefix="target"
                  />
                </SelectContent>
              </Select>
            </div>

            {/* #91 — see the source-side note above. */}
            <UnlistedDatabaseNotice
              connectionId={target.connectionId}
              database={target.database}
              databases={target.databases}
              state={target.databasesState}
              isLoading={target.isLoadingDatabases}
              testIdPrefix="target"
            />

            {/* Soporte C2 — database-listing failure, visible without opening
                the (closed) dropdown above. */}
            {target.databasesState === 'error' && !target.isLoadingDatabases && (
              <div className="flex flex-col gap-1.5">
                <DatabaseListingFallbackBanner
                  connectionId={target.connectionId!}
                  variant="row"
                  trigger="list_failure"
                  data-testid="target-databases-fallback-banner"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 self-start px-2 text-xs"
                  onClick={retryTargetDatabases}
                  data-testid="target-databases-retry"
                >
                  Retry
                </Button>
              </div>
            )}

            {/* Schema */}
            {showTargetSchemaSelector && (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Layers className="h-3 w-3" />
                  Schema
                </label>
                {/* Soporte C3 — see the source panel's comment above. */}
                <Select
                  value={target.schema ?? ''}
                  onValueChange={handleTargetSchemaChange}
                >
                  <SelectTrigger className="w-full" data-testid="target-schema-select">
                    {target.isLoadingTables ? (
                      <ListingLoadingLabel target="tables" />
                    ) : (
                      <SelectValue placeholder="Select schema..." />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {/* Soporte C3 fix-before-merge — see the source panel's
                        comment above. */}
                    {target.isLoadingTables ? (
                      <SelectItem value="_loading" disabled data-testid="target-schema-listing-loading">
                        <ListingLoadingLabel target="tables" />
                      </SelectItem>
                    ) : (
                      targetSchemas.map((schema) => (
                        <SelectItem key={schema} value={schema}>
                          {schema || '(default)'}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Soporte C2 — table-listing failure. Not gated on
                `!showTargetSchemaSelector` — see the source panel's comment
                above (prefilled `target.schema` from a context-menu launch
                would otherwise hide this error), nor on `!isSqliteTarget`
                (C4 — SQLite really does run this listing). */}
            {target.database &&
              !target.isLoadingTables && target.tablesError && (
              <div
                className="flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive"
                data-testid="target-tables-error"
              >
                <span className="flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Couldn&apos;t load schema/table info for this database.
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 shrink-0 px-2 text-xs"
                  onClick={retryTargetTables}
                  data-testid="target-tables-retry"
                >
                  Retry
                </Button>
              </div>
            )}

            {/* Table */}
            {showTargetTableSelector && (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Table2 className="h-3 w-3" />
                  Table
                </label>
                {/* `portal` — see the source panel's comment above. */}
                <SearchableSelect
                  value={target.table ?? ''}
                  onValueChange={handleTargetTableChange}
                  options={targetFilteredTables.map((table) => ({
                    value: table.name,
                    label: table.name,
                  }))}
                  placeholder="Select table..."
                  searchPlaceholder="Search tables..."
                  portal
                  data-testid="target-table-select"
                />
              </div>
            )}
          </div>
        </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleOpenCompare} disabled={!canOpen} data-testid="open-compare-button">
            Open Compare
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
